import { useEffect, useState, useRef } from 'react';
import { getBackendOrigin } from '../utils/apiConfig';

const useRealTimeUpdates = (token) => {
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);

  const connect = () => {
    try {
      const backendOrigin = getBackendOrigin();
      const url = new URL('/ws/mover-updates', backendOrigin);
      const wsUrl = url.protocol === 'https:' ? `wss://${url.host}/ws/mover-updates` : `ws://${url.host}/ws/mover-updates`;

      ws.current = new WebSocket(wsUrl);
      
      ws.current.onopen = () => {
        console.log('WebSocket connected');
        setConnectionStatus('connected');
        
        // Send authentication token
        if (token && token !== 'cookie') {
          ws.current.send(JSON.stringify({
            type: 'auth',
            token: token
          }));
        }
      };

      ws.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          switch (message.type) {
            case 'booking_update':
              setBookings(prev => {
                const updated = prev.map(booking => 
                  booking._id === message.data._id ? { ...booking, ...message.data } : booking
                );
                return updated;
              });
              break;
              
            case 'new_booking':
              setBookings(prev => [message.data, ...prev]);
              setNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'success',
                message: `New booking from ${message.data.customerName}`,
                timestamp: new Date()
              }]);
              break;
              
            case 'booking_cancelled':
              setBookings(prev => 
                prev.map(booking => 
                  booking._id === message.data.bookingId 
                    ? { ...booking, status: 'cancelled' } 
                    : booking
                )
              );
              setNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'warning',
                message: `Booking ${message.data.jobNumber} was cancelled`,
                timestamp: new Date()
              }]);
              break;
              
            case 'new_review':
              setNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'info',
                message: `New review: ${message.data.rating} stars`,
                timestamp: new Date()
              }]);
              break;
              
            case 'payment_received':
              setNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'success',
                message: `Payment received: $${message.data.amount}`,
                timestamp: new Date()
              }]);
              break;
              
            default:
              console.log('Unknown message type:', message.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.current.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setConnectionStatus('disconnected');
        
        // Attempt to reconnect after 3 seconds
        if (event.code !== 1000) { // Not a normal closure
          reconnectTimeout.current = setTimeout(() => {
            console.log('Attempting to reconnect...');
            connect();
          }, 3000);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionStatus('error');
    }
  };

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        ws.current.close(1000, 'Component unmounting');
      }
    };
  }, [token]);

  const sendMessage = (message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    }
  };

  const markNotificationRead = (notificationId) => {
    setNotifications(prev => 
      prev.filter(notification => notification.id !== notificationId)
    );
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  return {
    bookings,
    setBookings,
    notifications,
    connectionStatus,
    sendMessage,
    markNotificationRead,
    clearAllNotifications
  };
};

export default useRealTimeUpdates;